import React, {
    useState,
    useEffect,
    useReducer,
    useCallback,
    useContext,
} from "react";
import { toast } from "react-toastify";

import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar
} from "@mui/material";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import FileModal from "../../components/FileModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";

const reducer = (state, action) => {
    if (action.type === "LOAD_FILES") {
        const files = action.payload;
        const newFiles = [];

        files.forEach((fileList) => {
            const fileListIndex = state.findIndex((s) => s.id === fileList.id);
            if (fileListIndex !== -1) {
                state[fileListIndex] = fileList;
            } else {
                newFiles.push(fileList);
            }
        });

        return [...state, ...newFiles];
    }

    if (action.type === "UPDATE_FILES") {
        const fileList = action.payload;
        const fileListIndex = state.findIndex((s) => s.id === fileList.id);

        if (fileListIndex !== -1) {
            state[fileListIndex] = fileList;
            return [...state];
        } else {
            return [fileList, ...state];
        }
    }

    if (action.type === "DELETE_FILE") {
        const fileListId = action.payload;
        const fileListIndex = state.findIndex((s) => s.id === fileListId);
        if (fileListIndex !== -1) {
            state.splice(fileListIndex, 1);
        }
        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const FileLists = () => {
    const themeV4 = useThemeV4();
    const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
    const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";

    const { user, socket } = useContext(AuthContext);

    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [selectedFileList, setSelectedFileList] = useState(null);
    const [deletingFileList, setDeletingFileList] = useState(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [searchParam, setSearchParam] = useState("");
    const [files, dispatch] = useReducer(reducer, []);
    const [fileListModalOpen, setFileListModalOpen] = useState(false);

    const fetchFileLists = useCallback(async () => {
        try {
            const { data } = await api.get("/files/", {
                params: { searchParam, pageNumber },
            });
            dispatch({ type: "LOAD_FILES", payload: data.files });
            setHasMore(data.hasMore);
            setLoading(false);
        } catch (err) {
            toastError(err);
        }
    }, [searchParam, pageNumber]);

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, [searchParam]);

    useEffect(() => {
        setLoading(true);
        const delayDebounceFn = setTimeout(() => {
            fetchFileLists();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchParam, pageNumber, fetchFileLists]);

    useEffect(() => {
        const onFileEvent = (data) => {
            if (data.action === "update" || data.action === "create") {
                dispatch({ type: "UPDATE_FILES", payload: data.files });
            }

            if (data.action === "delete") {
                dispatch({ type: "DELETE_FILE", payload: +data.fileId });
            }
        };

        socket.on(`company-${user.companyId}-file`, onFileEvent);
        return () => {
            socket.off(`company-${user.companyId}-file`, onFileEvent);
        };
    }, [socket, user.companyId]);

    const handleOpenFileListModal = () => {
        setSelectedFileList(null);
        setFileListModalOpen(true);
    };

    const handleCloseFileListModal = () => {
        setSelectedFileList(null);
        setFileListModalOpen(false);
    };

    const handleSearch = (event) => {
        setSearchParam(event.target.value.toLowerCase());
    };

    const handleEditFileList = (fileList) => {
        setSelectedFileList(fileList);
        setFileListModalOpen(true);
    };

    const handleDeleteFileList = async (fileListId) => {
        try {
            await api.delete(`/files/${fileListId}`);
            toast.success(i18n.t("files.toasts.deleted"));
        } catch (err) {
            toastError(err);
        }
        setDeletingFileList(null);
        setSearchParam("");
        setPageNumber(1);

        dispatch({ type: "RESET" });
        setPageNumber(1);
        await fetchFileLists();
    };

    const loadMore = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    const handleScroll = (e) => {
        if (!hasMore || loading) return;
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            loadMore();
        }
    };

    if (user.profile === "user") {
        return <ForbiddenPage />;
    }

    return (
        <Box
            sx={{
                minHeight: "100vh",
                py: { xs: 3, md: 4 },
                px: { xs: 2, md: 3 },
                bgcolor: "#f8fafc"
            }}
        >
            <ConfirmationModal
                title={deletingFileList && `${i18n.t("files.confirmationModal.deleteTitle")}`}
                open={confirmModalOpen}
                onClose={setConfirmModalOpen}
                onConfirm={() => handleDeleteFileList(deletingFileList.id)}
            >
                {i18n.t("files.confirmationModal.deleteMessage")}
            </ConfirmationModal>

            <FileModal
                open={fileListModalOpen}
                onClose={handleCloseFileListModal}
                reload={fetchFileLists}
                aria-labelledby="form-dialog-title"
                fileListId={selectedFileList && selectedFileList.id}
            />

            <Container maxWidth="xl">
                {/* Header */}
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                    spacing={3}
                    sx={{ mb: 4 }}
                >
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 3,
                                bgcolor: PRIMARY_MAIN,
                                "& svg": { width: 28, height: 28, color: "#fff" }
                            }}
                        >
                            <FolderOpenIcon />
                        </Avatar>
                        <Box>
                            <Typography
                                variant="h4"
                                fontWeight={700}
                                sx={{
                                    color: "text.primary",
                                    mb: 0.5,
                                    letterSpacing: "-0.5px",
                                    fontSize: { xs: "1.5rem", md: "1.875rem" }
                                }}
                            >
                                {i18n.t("files.title")} ({files.length})
                            </Typography>
                            <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.875rem" }}>
                                Gerencie seus arquivos e documentos
                            </Typography>
                        </Box>
                    </Stack>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            placeholder={i18n.t("contacts.searchPlaceholder")}
                            type="search"
                            size="small"
                            value={searchParam}
                            onChange={handleSearch}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: "text.disabled", fontSize: 20 }} />
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                width: { xs: 200, sm: 280 },
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    bgcolor: "#fff",
                                    fontSize: "0.875rem",
                                    "& fieldset": { borderColor: "divider" },
                                    "&:hover fieldset": { borderColor: PRIMARY_MAIN },
                                    "&.Mui-focused fieldset": { borderColor: PRIMARY_MAIN }
                                }
                            }}
                        />

                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleOpenFileListModal}
                            sx={{
                                bgcolor: PRIMARY_MAIN,
                                color: "#fff",
                                borderRadius: 2,
                                px: 3,
                                py: 1,
                                fontWeight: 600,
                                fontSize: "0.875rem",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                                textTransform: "none",
                                "&:hover": {
                                    bgcolor: PRIMARY_DARK,
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.18)"
                                }
                            }}
                        >
                            {i18n.t("files.buttons.add")}
                        </Button>
                    </Stack>
                </Stack>

                {/* Tabela */}
                <Paper
                    elevation={0}
                    onScroll={handleScroll}
                    sx={{
                        borderRadius: 3,
                        bgcolor: "#fff",
                        border: "1px solid",
                        borderColor: "divider",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                        overflow: "hidden"
                    }}
                >
                    <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
                        <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.125rem" }}>
                            Todos os Arquivos
                        </Typography>
                    </Box>

                    <TableContainer sx={{ maxHeight: "calc(100vh - 280px)" }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        align="center"
                                        sx={{
                                            fontWeight: 600,
                                            textTransform: "uppercase",
                                            fontSize: "0.7rem",
                                            color: "text.secondary",
                                            letterSpacing: "0.5px",
                                            bgcolor: "background.paper"
                                        }}
                                    >
                                        {i18n.t("files.table.name")}
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{
                                            fontWeight: 600,
                                            textTransform: "uppercase",
                                            fontSize: "0.7rem",
                                            color: "text.secondary",
                                            letterSpacing: "0.5px",
                                            bgcolor: "background.paper"
                                        }}
                                    >
                                        {i18n.t("files.table.actions")}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {files.map((fileList) => (
                                    <TableRow
                                        key={fileList.id}
                                        sx={{
                                            "&:hover": { bgcolor: "action.hover", transition: "background-color 0.2s" }
                                        }}
                                    >
                                        <TableCell align="center">
                                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                                                {fileList.name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={0.5} justifyContent="center">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditFileList(fileList)}
                                                    sx={{
                                                        color: "text.secondary",
                                                        "&:hover": { color: PRIMARY_MAIN, bgcolor: "rgba(25, 118, 210, 0.08)" }
                                                    }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>

                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        setConfirmModalOpen(true);
                                                        setDeletingFileList(fileList);
                                                    }}
                                                    sx={{
                                                        color: "text.secondary",
                                                        "&:hover": { color: "#ef4444", bgcolor: "rgba(239, 68, 68, 0.08)" }
                                                    }}
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {loading && <TableRowSkeleton columns={2} />}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Container>
        </Box>
    );
};

export default FileLists;